import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useQueryClient } from "@tanstack/react-query";

import { api, ApiError, DEFAULT_REPLAY_RATE, type ClipResult, type ClipView } from "../api";
import { fisheyeFocus } from "./FisheyePlayer";
import { friendlyError } from "../errors";
import { useRole } from "../hooks/useRole";

export type RecordingDownloadStatus =
  | "preparing"
  | "paused"
  | "ready"
  | "failed"
  | "cancelled";

export type RecordingDownloadJob = {
  id: string;
  backendJobId: string;
  cameraId: string;
  cameraName: string;
  startMs: number;
  endMs: number;
  preS: number;
  postS: number;
  rate: number;
  startedAt: number;
  status: RecordingDownloadStatus;
  clip: ClipResult | null;
  error: string | null;
  thumbUrl?: string;
  // The fisheye view the clip is cut to (null: the whole picture).
  view: ClipView | null;
};

type StartInput = {
  cameraId: string;
  cameraName: string;
  startMs: number;
  endMs: number;
  rate: number;
  preS?: number;
  postS?: number;
  thumbUrl?: string;
  /** Fisheye view to cut the clip to. Omitted: whatever that camera's
   *  fisheye player shows right now (the whole picture if none is open). */
  view?: ClipView | null;
};

type RecordingDownloadsValue = {
  jobs: RecordingDownloadJob[];
  startDownload: (input: StartInput) => string;
  cancelDownload: (id: string) => void;
  pauseDownload: (id: string) => void;
  resumeDownload: (id: string) => void;
  pauseAll: () => void;
  resumeAll: () => void;
  dismissDownload: (id: string) => void;
};

const Ctx = createContext<RecordingDownloadsValue | null>(null);

export function useRecordingDownloads(): RecordingDownloadsValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("RecordingDownloadsProvider missing");
  return v;
}

const MAX_JOBS = 20;

function newId(prefix: string): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function saveClip(clip: ClipResult) {
  const link = document.createElement("a");
  link.href = clip.url;
  link.download = clip.filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function patchJob(
  jobs: RecordingDownloadJob[],
  id: string,
  patch: Partial<RecordingDownloadJob>,
): RecordingDownloadJob[] {
  return jobs.map((job) => (job.id === id ? { ...job, ...patch } : job));
}

export function RecordingDownloadsProvider({ children }: { children: ReactNode }) {
  const { role } = useRole();
  const qc = useQueryClient();
  const [jobs, setJobs] = useState<RecordingDownloadJob[]>([]);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  const controllers = useRef(new Map<string, AbortController>());

  const stopRun = useCallback((id: string, backendJobId?: string) => {
    const ac = controllers.current.get(id);
    controllers.current.delete(id);
    ac?.abort();
    const jobId =
      backendJobId ?? jobsRef.current.find((job) => job.id === id)?.backendJobId;
    if (jobId) void api.cancelClip(jobId);
  }, []);

  const runClip = useCallback(
    (id: string, input: StartInput, backendJobId: string) => {
      const ac = new AbortController();
      controllers.current.set(id, ac);
      void api
        .clip(
          {
            camera_id: input.cameraId,
            start_ms: input.startMs,
            end_ms: input.endMs,
            pre_s: input.preS ?? 0,
            post_s: input.postS ?? 0,
            replay_rate: input.rate || DEFAULT_REPLAY_RATE,
            job_id: backendJobId,
            save_download: true,
            thumb_url: input.thumbUrl,
            ...(input.view ? { view: input.view } : {}),
          },
          ac.signal,
        )
        .then((clip) => {
          if (ac.signal.aborted) return;
          const current = jobsRef.current.find((job) => job.id === id);
          if (!current || current.status !== "preparing") return;
          if (current.backendJobId !== backendJobId) return;
          controllers.current.delete(id);
          setJobs((prev) =>
            patchJob(prev, id, { status: "ready", clip, error: null }),
          );
          saveClip(clip);
          void qc.invalidateQueries({ queryKey: ["saved-clips"] });
        })
        .catch((err) => {
          if (ac.signal.aborted) return;
          if (err instanceof ApiError && err.status === 499) return;
          const current = jobsRef.current.find((job) => job.id === id);
          if (
            !current ||
            current.status === "paused" ||
            current.status === "cancelled"
          ) {
            return;
          }
          controllers.current.delete(id);
          setJobs((prev) =>
            patchJob(prev, id, {
              status: "failed",
              error:
                err instanceof ApiError
                  ? friendlyError(err, role, "Could not prepare that download.")
                  : "Could not prepare that download.",
            }),
          );
        });
    },
    [role, qc],
  );

  const cancelDownload = useCallback(
    (id: string) => {
      stopRun(id);
      setJobs((prev) =>
        prev.map((job) =>
          job.id === id &&
          (job.status === "preparing" || job.status === "paused")
            ? { ...job, status: "cancelled" as const }
            : job,
        ),
      );
    },
    [stopRun],
  );

  const pauseDownload = useCallback(
    (id: string) => {
      const job = jobsRef.current.find((row) => row.id === id);
      if (!job || job.status !== "preparing") return;
      stopRun(id, job.backendJobId);
      setJobs((prev) =>
        prev.map((row) =>
          row.id === id && row.status === "preparing"
            ? { ...row, status: "paused" as const }
            : row,
        ),
      );
    },
    [stopRun],
  );

  const resumeDownload = useCallback(
    (id: string) => {
      const job = jobsRef.current.find((row) => row.id === id);
      if (!job || job.status !== "paused") return;
      const backendJobId = newId("clip");
      setJobs((prev) =>
        patchJob(prev, id, {
          status: "preparing",
          startedAt: Date.now(),
          backendJobId,
          error: null,
        }),
      );
      runClip(
        id,
        {
          cameraId: job.cameraId,
          cameraName: job.cameraName,
          startMs: job.startMs,
          endMs: job.endMs,
          preS: job.preS,
          postS: job.postS,
          rate: job.rate,
          thumbUrl: job.thumbUrl,
          view: job.view,
        },
        backendJobId,
      );
    },
    [runClip],
  );

  const pauseAll = useCallback(() => {
    for (const job of jobsRef.current) {
      if (job.status === "preparing") pauseDownload(job.id);
    }
  }, [pauseDownload]);

  const resumeAll = useCallback(() => {
    for (const job of jobsRef.current) {
      if (job.status === "paused") resumeDownload(job.id);
    }
  }, [resumeDownload]);

  const startDownload = useCallback(
    (request: StartInput) => {
      const id = newId("dl");
      const backendJobId = newId("clip");
      const input: StartInput = {
        ...request,
        view: request.view !== undefined ? request.view : (fisheyeFocus(request.cameraId) ?? null),
      };
      for (const job of jobsRef.current) {
        if (job.cameraId === input.cameraId && job.status === "preparing") {
          stopRun(job.id, job.backendJobId);
        }
      }
      setJobs((prev) => {
        const next: RecordingDownloadJob = {
          id,
          backendJobId,
          cameraId: input.cameraId,
          cameraName: input.cameraName,
          startMs: input.startMs,
          endMs: input.endMs,
          preS: input.preS ?? 0,
          postS: input.postS ?? 0,
          rate: input.rate,
          startedAt: Date.now(),
          status: "preparing",
          clip: null,
          error: null,
          thumbUrl: input.thumbUrl,
          view: input.view ?? null,
        };
        return [
          next,
          ...prev.map((job) =>
            job.cameraId === input.cameraId && job.status === "preparing"
              ? { ...job, status: "cancelled" as const }
              : job,
          ),
        ].slice(0, MAX_JOBS);
      });
      runClip(id, input, backendJobId);
      return id;
    },
    [runClip, stopRun],
  );

  const dismissDownload = useCallback(
    (id: string) => {
      stopRun(id);
      setJobs((prev) => prev.filter((job) => job.id !== id));
    },
    [stopRun],
  );

  const value = useMemo(
    () => ({
      jobs,
      startDownload,
      cancelDownload,
      pauseDownload,
      resumeDownload,
      pauseAll,
      resumeAll,
      dismissDownload,
    }),
    [
      jobs,
      startDownload,
      cancelDownload,
      pauseDownload,
      resumeDownload,
      pauseAll,
      resumeAll,
      dismissDownload,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
